<template>
  <SettingsSection>
    <SettingsSectionToggle
      :title="general.title"
      :description="general.description"
      :isEnabled="sessionStartSoundNotification.enabled"
      @click="updatePreference('sessionStartSoundNotification', 'enabled', !sessionStartSoundNotification.enabled)"
    />
    <SettingsSectionSeparatorLine v-if="sessionStartSoundNotification.enabled" />
    <SettingsSectionDropdownSelect
      v-if="sessionStartSoundNotification.enabled"
      :currentSelection="sessionStartSoundNotification.reminderTimeBeforeSessionMS"
      :options="sessionStartSoundNoticeTime.options"
      :title="sessionStartSoundNoticeTime.title"
      :description="sessionStartSoundNoticeTime.description"
      @selected="updatePreference('sessionStartSoundNotification', 'reminderTimeBeforeSessionMS', $event)"
    />
    <SettingsSectionSeparatorLine v-if="sessionStartSoundNotification.enabled" />
    <SettingsSectionDropdownSelect
      v-if="sessionStartSoundNotification.enabled"
      :currentSelection="sessionStartSoundNotification.sound"
      :options="sessionStartSound.options"
      :title="sessionStartSound.title"
      :description="sessionStartSound.description"
      @selected="updatePreference('sessionStartSoundNotification', 'sound', $event)"
    />
    <SettingsSectionSeparatorLine v-if="sessionStartSoundNotification.enabled" />
    <SettingsSectionDropdownSelect
      v-if="sessionStartSoundNotification.enabled"
      :currentSelection="sessionStartSoundNotification.soundVolume"
      :options="sessionStartSoundVolume.options"
      :title="sessionStartSoundVolume.title"
      :description="sessionStartSoundVolume.description"
      @selected="updatePreference('sessionStartSoundNotification', 'soundVolume', $event)"
    />
    <SettingsSectionDisclaimer>
      Note that you currently must have at least one tab with the calendar page open for this to work. Additionally, browsers require you to first
      interact with the page each time you open the website in a new tab (such as by clicking literally anything), to give Cofocus permission to play
      a sound.
    </SettingsSectionDisclaimer>
  </SettingsSection>
</template>

<script>
import { mapState } from "vuex";
import SettingsAPI from "./SettingsAPI";
import SettingsSection from "./SettingsSection.vue";
import SettingsSectionToggle from "./SettingsSectionToggle.vue";
import SettingsSectionDropdownSelect from "./SettingsSectionDropdownSelect.vue";
import SettingsSectionSeparatorLine from "./SettingsSectionSeparatorLine.vue";
import CalendarUtilities from "../Calendar/CalendarUtilities";
import ELYXUtils from "../ELYXUtils";
import SettingsSectionDisclaimer from "./SettingsSectionDisclaimer.vue";

export default {
  components: {
    SettingsSection,
    SettingsSectionToggle,
    SettingsSectionDropdownSelect,
    SettingsSectionSeparatorLine,
    SettingsSectionDisclaimer,
  },
  data() {
    return {
      general: {
        title: "Session start notification sound",
        description:
          "Choose whether you would like to hear a notification sound before your session starts. Useful if you're in another tab and lose track of time.",
        enabled: true,
      },
      sessionStartSoundNoticeTime: {
        title: "Notification time",
        description: "Choose how much time before your session starts you'd like to hear the notification sound.",
        options: [
          { value: 30000, label: "30 seconds" },
          { value: 60000, label: "1 minute" },
          { value: 120000, label: "2 minutes" },
          { value: 180000, label: "3 minutes" },
          { value: 300000, label: "5 minutes" },
        ],
      },
      sessionStartSound: {
        title: "Notification sound",
        description: "Try out different sounds and choose the one you'd like to hear when a session is about to start.",
        options: [
          { value: 0, label: "Beginnings" },
          { value: 1, label: "Twinkles" },
          { value: 2, label: "Bells" },
          { value: 3, label: "Many Bells" },
          { value: 4, label: "Chords" },
          { value: 5, label: "Dramatic Ascent" },
          { value: 6, label: "Magical Sprinkles" },
          { value: 7, label: "Mystical Gong" },
          { value: 8, label: "Light Gong" },
          { value: 9, label: "Pleasant Announcement" },
          { value: 10, label: "Sudden Announcement" },
          { value: 11, label: "Stinger" },
          { value: 12, label: "Synth" },
          { value: 13, label: "The Heavens" },
          { value: 14, label: "Good Things Ahead" },
          { value: 15, label: "Because Why Not" },
          { value: 16, label: "I Am An Agent" },
        ],
      },
      sessionStartSoundVolume: {
        title: "Notification sound volume",
        description: "Choose how loud you want the sound to when a session is about to start.",
        options: [
          { value: 0.2, label: "Low" },
          { value: 0.5, label: "Medium" },
          { value: 0.8, label: "High" },
        ],
      },
    };
  },
  computed: {
    ...mapState({
      user: (state) => state.auth.user,
      loaded: (state) => state.emailPreferences.loaded,
    }),
    userNotificationPreferences() {
      return this.user.notificationPreferences;
    },
    sessionStartSoundNotification() {
      return this.userNotificationPreferences.sessionStartSoundNotification;
    },
  },
  methods: {
    async updatePreference(type, field, newValue) {
      if (field === "sound") {
        const sound = await CalendarUtilities.getSessionNotificationSound(newValue);
        if (sound && sound.default) {
          ELYXUtils.playSound(sound, this.userNotificationPreferences.sessionStartSoundVolume);
        }
      } else if (field === "soundVolume") {
        const sound = await CalendarUtilities.getSessionNotificationSound(this.sessionStartSoundNotification.sound);
        if (sound && sound.default) {
          ELYXUtils.playSound(sound, newValue);
        }
      }

      /* Update email preference in local storage */
      const updateData = { type, field, newValue };
      await this.$store.dispatch("auth/updateUserNotificationPreferences", updateData);

      /* Send update request with new preferences */
      const updatedNotificationPreferences = this.userNotificationPreferences;
      const response = await SettingsAPI.updateUserNotificationPreferences(updatedNotificationPreferences, this.user._id);
      if (!response || !response.success) {
        throw new Error("Failed to update user notification preferences");
      }
    },
  },
};
</script>
